import { decodePersonalViews, normalizePersonalView, normalizeViewName, PERSONAL_VIEW_LIMIT, PERSONAL_VIEW_MAX_CHARS, type ViewOperationError } from './tableViews'
import type { PersonalTableViewV1, TableDefinition, TableFilterValue, TablePreferenceScope } from './tableState'
import { defaultHiddenColumns, normalizeHiddenColumns } from './tableState'

/** Browser-local presentation preferences. Never store authority or domain data here. */
export const INTERFACE_DENSITIES = ['compact', 'comfortable', 'spacious'] as const
export type InterfaceDensity = typeof INTERFACE_DENSITIES[number]
export const DEFAULT_INTERFACE_DENSITY: InterfaceDensity = 'comfortable'

export interface DensityPreferenceScope {
  userId: string
  tenantId: string
  workspaceId: string
}

export interface DensitySnapshot {
  density: InterfaceDensity
  persisted: boolean
}

const preferenceEvent = 'punch:interface-density'
const isDensity = (value: unknown): value is InterfaceDensity =>
  INTERFACE_DENSITIES.some(density => density === value)

const interfacePreferenceKey = (kind: string, scope: readonly unknown[]) =>
  `punch.productivity.${kind}.v1:${JSON.stringify(scope)}`

export function densityPreferenceKey(scope: DensityPreferenceScope | null): string | null {
  if (!scope?.userId || !scope.tenantId || !scope.workspaceId) return null
  // Tuple encoding avoids delimiter collisions between users/tenants/workspaces.
  return interfacePreferenceKey('density', [scope.userId, scope.tenantId, scope.workspaceId])
}

interface PreferenceCodec<S extends { persisted: boolean }> {
  fallback: Omit<S, 'persisted'>
  decode: (value: unknown) => Omit<S, 'persisted'>
  valid: (value: unknown) => value is S
  encode: (value: S) => unknown
}

/** Shared persistence/lifecycle core for all adopted interface preferences. */
function createInterfacePreferenceStore<S extends { persisted: boolean }>(key: string | null,
  eventName: string, codec: PreferenceCodec<S>) {
  const read = (): S => {
    const fallback = { ...codec.fallback, persisted: false } as S
    if (!key || typeof window === 'undefined') return fallback
    try {
      const raw = window.localStorage.getItem(key)
      let value: unknown = null
      try { value = raw && raw.length <= 20000 ? JSON.parse(raw) : null } catch { /* Malformed preferences use defaults. */ }
      return { ...codec.decode(value), persisted: true } as S
    } catch { return fallback }
  }
  let snapshot = read()
  const listeners = new Set<() => void>()
  const update = (next: S) => {
    if (JSON.stringify(snapshot) === JSON.stringify(next)) return
    snapshot = next
    listeners.forEach(listener => listener())
  }
  const onStorage = (event: StorageEvent) => {
    if (key && (event.key === key || event.key === null)) update(read())
  }
  const onPreference = (event: Event) => {
    const change = (event as CustomEvent<{ key: string; snapshot: unknown }>).detail
    if (key && change?.key === key && codec.valid(change.snapshot)) update(change.snapshot)
  }
  return {
    getSnapshot: () => snapshot,
    readLatest: read,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      if (listeners.size === 1 && key && typeof window !== 'undefined') {
        window.addEventListener('storage', onStorage)
        window.addEventListener(eventName, onPreference)
        if (snapshot.persisted) update(read())
      }
      return () => {
        listeners.delete(listener)
        if (listeners.size === 0 && typeof window !== 'undefined') {
          window.removeEventListener('storage', onStorage)
          window.removeEventListener(eventName, onPreference)
        }
      }
    },
    setSnapshot: (value: S) => {
      if (!key || !codec.valid(value) || typeof window === 'undefined') return
      let persisted = false
      try {
        window.localStorage.setItem(key, JSON.stringify(codec.encode(value)))
        persisted = true
      } catch { /* Keep explicit in-memory state when browser storage is unavailable. */ }
      update({ ...value, persisted })
      window.dispatchEvent(new CustomEvent(eventName, { detail: { key, snapshot } }))
    },
  }
}

export function createDensityPreferenceStore(key: string | null) {
  const store = createInterfacePreferenceStore<DensitySnapshot>(key, preferenceEvent, {
    fallback: { density: DEFAULT_INTERFACE_DENSITY },
    decode: value => {
      const record = value as { version?: unknown; density?: unknown } | null
      return { density: record?.version === 1 && isDensity(record.density) ? record.density : DEFAULT_INTERFACE_DENSITY }
    },
    valid: (value): value is DensitySnapshot => {
      const record = value as DensitySnapshot | null
      return Boolean(record && isDensity(record.density) && typeof record.persisted === 'boolean')
    },
    encode: value => ({ version: 1, density: value.density }),
  })
  return { getSnapshot: store.getSnapshot, subscribe: store.subscribe,
    setDensity: (density: InterfaceDensity) => {
      if (isDensity(density)) store.setSnapshot({ density, persisted: false })
    },
  }
}

export const RECENT_COMMAND_LIMIT = 8
export interface CommandHistoryScope {
  userId: string
  /** Null is an explicitly verified tenantless context, never unresolved identity. */
  tenantId: string | null
  workspaceId: string
  organizationId?: string | null
}
export interface RecentCommandSnapshot { commands: readonly string[]; persisted: boolean }
const isCommandId = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,159}$/.test(value)
export function normalizeRecentCommands(value: readonly unknown[]): string[] {
  return Array.from(new Set(value.filter(isCommandId))).slice(0, RECENT_COMMAND_LIMIT)
}
export function recentCommandPreferenceKey(scope: CommandHistoryScope | null): string | null {
  if (!scope || typeof scope.userId !== 'string' || !scope.userId || typeof scope.workspaceId !== 'string' || !scope.workspaceId
    || (scope.tenantId !== null && (typeof scope.tenantId !== 'string' || !scope.tenantId))) return null
  return interfacePreferenceKey('recent-commands', [scope.userId,
    scope.tenantId === null ? ['tenantless'] : ['tenant', scope.tenantId], scope.workspaceId, scope.organizationId || null])
}
export function createRecentCommandPreferenceStore(key: string | null) {
  const store = createInterfacePreferenceStore<RecentCommandSnapshot>(key, 'punch:recent-commands', {
    fallback: { commands: [] },
    decode: value => {
      const record = value as { version?: unknown; commands?: unknown } | null
      return { commands: record?.version === 1 && Array.isArray(record.commands) ? normalizeRecentCommands(record.commands) : [] }
    },
    valid: (value): value is RecentCommandSnapshot => {
      const record = value as RecentCommandSnapshot | null
      return Boolean(record && typeof record.persisted === 'boolean' && Array.isArray(record.commands)
        && record.commands.length <= RECENT_COMMAND_LIMIT && record.commands.every(isCommandId)
        && new Set(record.commands).size === record.commands.length)
    },
    encode: value => ({ version: 1, commands: value.commands }),
  })
  return { getSnapshot: store.getSnapshot, subscribe: store.subscribe,
    enabled: key !== null,
    record: (id: string) => {
      if (!key || !isCommandId(id)) return
      store.setSnapshot({ commands: normalizeRecentCommands([id, ...store.getSnapshot().commands, ...store.readLatest().commands]), persisted: false })
    },
  }
}

/** Table preferences reuse the Phase 1 codec/subscription/storage lifecycle. */
export interface TableColumnSnapshot { hiddenColumnIds: readonly string[]; persisted: boolean }
export function tableColumnPreferenceKey(scope: import('./tableState').TablePreferenceScope | null): string | null {
  if (!scope || ![scope.userId, scope.workspaceId, scope.tableId].every(value => typeof value === 'string' && value.length > 0)
    || (scope.tenantId !== null && (typeof scope.tenantId !== 'string' || !scope.tenantId))
    || (scope.organizationId !== null && (typeof scope.organizationId !== 'string' || !scope.organizationId))) return null
  return interfacePreferenceKey('table-columns', [scope.userId, scope.tenantId === null ? ['tenantless'] : ['tenant', scope.tenantId],
    scope.workspaceId, scope.organizationId, scope.tableId])
}
export function createTableColumnPreferenceStore(key: string | null, columns: readonly import('./tableState').TableColumn[]) {
  const defaults = defaultHiddenColumns(columns)
  const normalize = (ids: readonly unknown[]) => normalizeHiddenColumns(ids, columns)
  const store = createInterfacePreferenceStore<TableColumnSnapshot>(key, 'punch:table-columns', {
    fallback: { hiddenColumnIds: defaults },
    decode: value => {
      const record = value as { version?: unknown; hiddenColumnIds?: unknown } | null
      return { hiddenColumnIds: record?.version === 1 && Array.isArray(record.hiddenColumnIds)
        ? normalize(record.hiddenColumnIds.slice(0, 200)) : defaults }
    },
    valid: (value): value is TableColumnSnapshot => {
      const record = value as TableColumnSnapshot | null
      return Boolean(record && typeof record.persisted === 'boolean' && Array.isArray(record.hiddenColumnIds)
        && record.hiddenColumnIds.length <= 200 && JSON.stringify(normalize(record.hiddenColumnIds)) === JSON.stringify(record.hiddenColumnIds))
    },
    encode: value => ({ version: 1, hiddenColumnIds: normalize(value.hiddenColumnIds) }),
  })
  return { getSnapshot: store.getSnapshot, subscribe: store.subscribe,
    setVisible: (id: string, visible: boolean) => {
      if (!columns.some(column => column.id === id && !column.required)) return
      // Read current cross-tab state; retain in-memory changes if writes were blocked.
      const mounted = store.getSnapshot()
      const latest = mounted.persisted ? store.readLatest() : mounted
      const current = latest.persisted ? latest : mounted
      store.setSnapshot({ hiddenColumnIds: normalize(visible ? current.hiddenColumnIds.filter(item => item !== id)
        : [...current.hiddenColumnIds, id]), persisted: false })
    },
    setHiddenColumnIds: (ids: readonly string[]) => store.setSnapshot({ hiddenColumnIds: normalize(ids), persisted: false }),
    restoreDefaults: () => store.setSnapshot({ hiddenColumnIds: defaults, persisted: false }),
  }
}

export interface PersonalViewsSnapshot { views: readonly PersonalTableViewV1[]; persisted: boolean }
export function personalViewsPreferenceKey(scope: TablePreferenceScope | null): string | null {
  const columnKey = tableColumnPreferenceKey(scope)
  return columnKey ? columnKey.replace('punch.productivity.table-columns.', 'punch.productivity.table-views.') : null
}
/** Reuse the existing bounded codec/subscription/storage lifecycle; no backend persistence. */
export function createPersonalViewsPreferenceStore<F extends Record<string, TableFilterValue>>(key: string | null, definition: TableDefinition<F>) {
  const store = createInterfacePreferenceStore<PersonalViewsSnapshot>(key, 'punch:table-views', {
    fallback: { views: [] },
    decode: value => ({ views: decodePersonalViews(value, definition) }),
    valid: (value): value is PersonalViewsSnapshot => {
      const record = value as PersonalViewsSnapshot | null
      return Boolean(record && typeof record.persisted === 'boolean' && Array.isArray(record.views)
        && JSON.stringify(record.views) === JSON.stringify(decodePersonalViews({version:1,views:record.views},definition))
        && JSON.stringify(record.views).length < PERSONAL_VIEW_MAX_CHARS - 100)
    },
    encode: value => ({version:1,views:value.views}),
  })
  // Read the freshest same-scope snapshot, retaining explicit mounted choices on storage failure.
  const latest = () => { const mounted=store.getSnapshot(), fresh=mounted.persisted?store.readLatest():mounted; return fresh.persisted?fresh:mounted }
  const save = (views: readonly PersonalTableViewV1[]) => store.setSnapshot({views,persisted:false})
  return { getSnapshot:store.getSnapshot,subscribe:store.subscribe,
    create:(view:PersonalTableViewV1): ViewOperationError | null => {
      const normalized=normalizePersonalView(view,definition), name=normalizeViewName(view.name), current=latest().views
      if(!normalized||!name)return 'invalid-name'
      if(current.some(item=>item.name.toLowerCase()===name.toLowerCase()))return 'duplicate-name'
      if(current.length>=PERSONAL_VIEW_LIMIT)return 'view-limit'
      save([...current,normalized]);return null
    },
    rename:(id:string,name:string): ViewOperationError | null => {
      const normalized=normalizeViewName(name),current=latest().views
      if(!normalized)return 'invalid-name'
      if(!current.some(item=>item.id===id))return 'missing-view'
      if(current.some(item=>item.id!==id&&item.name.toLowerCase()===normalized.toLowerCase()))return 'duplicate-name'
      save(current.map(item=>item.id===id?{...item,name:normalized}:item));return null
    },
    remove:(id:string)=>save(latest().views.filter(item=>item.id!==id)),
  }
}
